import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { category_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchBrandsAllQuery } from "../../../stores/api/brands.api";
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "../../../stores/api/categories.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";

const EMPTY = {
  brand_id: "",
  name: "",
  slug: "",
  description: "",
  image: null,
  image_alt: "",
  status: 1,
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
};

const toSlug = (str) => str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const CreateProductCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.category;

  const { data: brandsRaw = [] } = useFetchBrandsAllQuery();
  const brands = useMemo(
    () => (brandsRaw || []).map((b) => ({ value: String(b.id), label: b.name })),
    [brandsRaw]
  );

  const [createCategory, { isLoading: creating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: updating }] = useUpdateCategoryMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          brand_id: String(details.brand_id) || "",
          name: details.name || "",
          slug: details.slug || "",
          description: details.description || "",
          image_alt: details.image_alt || "",
          status: details.status === "Active" ? true : false,
          meta_title: details.meta_title || "",
          meta_description: details.meta_description || "",
          meta_keywords: details.meta_keywords || "",
        }
      : {}),
  });

  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "name" && !details) next.slug = toSlug(value);
      return next;
    });
  };

  const resetForm = () => setFormData(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = buildFormData({
        ...formData,
        status: formData.status ? 1 : 0,
      });

      if (details) {
        await updateCategory({ id: details.id, body }).unwrap();
      } else {
        await createCategory(body).unwrap();
      }

      ToastMessage({ type: "success", title: "Success", message: details ? "Category updated!" : "Category created!" });
      if (!details) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Product Category" : "Create Product Category"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Category Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Brand"  className="md:col-span-2" isRequired options={brands} value={formData.brand_id} onChange={(val) => setFormData((prev) => ({ ...prev, brand_id: val ?? "" }))} />
            <FormInput label="Name" name="name" value={formData.name} isRequired onChange={(val) => setField("name", val)} placeholder="Enter category name" />
            <FormInput label="Slug" name="slug" value={formData.slug} isRequired onChange={(val) => setField("slug", val)} placeholder="auto-generated-from-name" />
            <FormInput label="Description" isRequired name="description" value={formData.description} onChange={(val) => setFormData((prev) => ({ ...prev, description: val }))} placeholder="Short description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>

        <FormSection title="Category Image">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormImage
              label="Image"
              isRequired
              initialPreviewUrl={details?.image ? (category_image_url ?? "") + details.image : null}
              onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))}
            />
            <FormInput
              label="Image Alt Text"
              name="image_alt"
              value={formData.image_alt}
              isRequired
              onChange={(val) => setField("image_alt", val)}
              placeholder="Describe the image"
            />
          </div>
        </FormSection>

        <FormSection title="SEO / Meta">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Meta Title" isRequired name="meta_title" value={formData.meta_title} onChange={(val) => setFormData((prev) => ({ ...prev, meta_title: val }))} placeholder="SEO title" />
            <FormInput label="Meta Keywords"  name="meta_keywords" value={formData.meta_keywords} onChange={(val) => setFormData((prev) => ({ ...prev, meta_keywords: val }))} placeholder="keyword1, keyword2" />
            <FormInput label="Meta Description" isRequired name="meta_description" value={formData.meta_description} onChange={(val) => setFormData((prev) => ({ ...prev, meta_description: val }))} placeholder="SEO description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>
        <div>
            <FormSwitch label="Status" checked={formData.status} onChange={(val) => setFormData((prev) => ({ ...prev, status: val ? 1 : 0 }))} />
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" disabled={loading} onClick={() => { resetForm(); navigate(ROUTE_PATHS.PRODUCT_CATEGORY); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200">
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} className="!bg-green-500 !text-white hover:!bg-green-600" loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Category" : "Create Category"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateProductCategory;
